/**
 * Shared Brand Portal Sidebar
 *
 * A reusable sidebar navigation component that matches the Brand Portal layout.
 * Use this in any Brand surface prototype to provide consistent navigation context.
 *
 * Usage:
 *   import { BrandPortalSidebar } from "../../components/shared";
 *
 *   <BrandPortalLayout>
 *     <YourPageContent />
 *   </BrandPortalLayout>
 *
 * Or use the sidebar directly:
 *   <div className="flex">
 *     <BrandPortalSidebar activeItem="Products" />
 *     <main className="flex-1">...</main>
 *   </div>
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import {
  CaretDoubleRight,
  ChartLine,
  ChatCircle,
  Circle,
  GearSix,
  Headphones,
  House,
  Megaphone,
  Receipt,
  SquaresFour,
  Storefront,
  Users,
} from "@phosphor-icons/react";
import React, { useState } from "react";

type IconComponent = React.ComponentType<{
  size?: number;
  weight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
}>;

const NAV_ICONS: Record<string, IconComponent> = {
  Home: House,
  Orders: Receipt,
  Messages: ChatCircle,
  Products: SquaresFour,
  Customers: Users,
  "Faire Direct": CaretDoubleRight,
  Marketing: Megaphone,
  Analytics: ChartLine,
  "My shop": Storefront,
  Settings: GearSix,
  "Help Center": Headphones,
  "What's new": Megaphone,
};

const slateSans: React.CSSProperties = {
  fontFamily: "var(--slate-typography-family-sans, 'Graphik', sans-serif)",
};

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */

function ChevronIcon({ expanded }: { expanded?: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className={cn("transition-transform", expanded ? "rotate-90" : "")}
    >
      <path
        d="M6 4L10 8L6 12"
        stroke="var(--slate-color-text-subdued, #757575)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--slate-color-text-primary, #333)"
      strokeWidth="1.5"
    >
      <path
        d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.73 21a2 2 0 0 1-3.46 0"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Nav item types                                                     */
/* ------------------------------------------------------------------ */

interface NavItem {
  label: string;
  children?: string[];
  badge?: number;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Home" },
  { label: "Orders", badge: 1 },
  { label: "Messages" },
  {
    label: "Products",
    children: [
      "All products",
      "Collections",
      "Inventory",
      "Bulk upload",
      "Prepacks",
    ],
  },
  { label: "Customers" },
  { label: "Faire Direct" },
  {
    label: "Marketing",
    children: [
      "Campaigns",
      "Automations",
      "Promotions",
      "Promoted Listings",
      "Insider Partnership",
      "Events",
    ],
  },
  { label: "Analytics" },
  {
    label: "My shop",
    children: ["Brand page", "Policies"],
  },
  { label: "Settings" },
];

const FOOTER_ITEMS = ["Help Center", "What's new"];

/* ------------------------------------------------------------------ */
/*  Nav override resolver                                              */
/* ------------------------------------------------------------------ */

function resolveNavItems(
  baseline: NavItem[],
  overrides: NavItemOverride[]
): NavItem[] {
  if (overrides.length === 0) return baseline;

  // Start with a deep copy of baseline
  const items: NavItem[] = baseline.map((item) => ({
    ...item,
    children: item.children ? [...item.children] : undefined,
  }));

  for (const override of overrides) {
    const existingIndex = items.findIndex((i) => i.label === override.label);

    if (existingIndex !== -1) {
      // Modify existing item
      const existing = items[existingIndex];
      if (override.children) {
        existing.children = override.children;
      }
      if (override.addChildren) {
        const current = existing.children ?? [];
        const toAdd = override.addChildren.filter((c) => !current.includes(c));
        existing.children = [...current, ...toAdd];
      }
      if (override.badge !== undefined) {
        existing.badge = override.badge;
      }
    } else {
      // Insert new item
      const newItem: NavItem = {
        label: override.label,
        children: override.children ?? override.addChildren,
        badge: override.badge,
      };
      if (override.afterItem) {
        const afterIndex = items.findIndex(
          (i) => i.label === override.afterItem
        );
        if (afterIndex !== -1) {
          items.splice(afterIndex + 1, 0, newItem);
        } else {
          items.push(newItem);
        }
      } else {
        items.push(newItem);
      }
    }
  }

  return items;
}

/* ------------------------------------------------------------------ */
/*  BrandPortalSidebar                                                 */
/* ------------------------------------------------------------------ */

/**
 * Override a top-level nav item or inject new items/children.
 *
 * - To **add children** to an existing section:
 *   `{ label: "Products", addChildren: ["Dashboard", "Bulk upload v2"] }`
 *
 * - To **replace children** entirely:
 *   `{ label: "Products", children: ["Dashboard", "All products"] }`
 *
 * - To **insert a new top-level item** (placed after `afterItem`):
 *   `{ label: "Fulfilled by Faire", afterItem: "Products", children: [...] }`
 *
 * - To **add a badge**:
 *   `{ label: "Messages", badge: 35 }`
 */
export interface NavItemOverride {
  label: string;
  /** Insert this item after the given top-level label. Only used for new items. */
  afterItem?: string;
  /** Completely replace children for an existing section. */
  children?: string[];
  /** Append extra children to the existing list (merged, no duplicates). */
  addChildren?: string[];
  /** Override badge count. */
  badge?: number;
}

export type BrandPortalSidebarMode = "full" | "hidden" | "compact" | "quiet";

export interface BrandPortalSidebarProps {
  /** Which top-level nav item is active (e.g. "Products") */
  activeItem?: string;
  /** Which child nav item is active (e.g. "Bulk upload") */
  activeChild?: string;
  /** Avatar initials */
  initials?: string;
  /** Optional avatar image URL — when provided, replaces the initials circle. */
  avatarUrl?: string;
  /**
   * Prototype-specific nav overrides. Use this to add items, inject children,
   * or tweak the baseline nav without modifying the shared component.
   */
  navOverrides?: NavItemOverride[];
  /** Map of nav item label → route path for prototype-specific navigation. */
  navLinks?: Record<string, string>;
  navItemIds?: Record<string, string>;
  onNavItemClick?: (label: string) => void;
  /**
   * Optional text pill after a child label (e.g. Promotions → "New") for visual parity in prototypes.
   */
  childBadges?: Record<string, string>;
  /**
   * Rendering mode. Defaults to "full" (today's behavior).
   * - "hidden": ~72px column, no nav rendered, avatar at bottom, F-circle + bell + chevron header
   * - "compact": ~140px column, text-only nav, no badges/icons/chevrons, avatar at bottom
   * - "quiet": 256px column, all items, lighter styling, no badges/chevrons by default, avatar at bottom
   */
  mode?: BrandPortalSidebarMode;
}

export default function BrandPortalSidebar({
  activeItem,
  activeChild,
  initials = "AD",
  avatarUrl,
  navOverrides = [],
  navLinks = {},
  navItemIds = {},
  onNavItemClick,
  childBadges,
  mode = "full",
}: BrandPortalSidebarProps) {
  // Mode-specific renderers — see the bottom of this file for each implementation.
  if (mode === "hidden") {
    return <HiddenSidebar initials={initials} avatarUrl={avatarUrl} />;
  }
  if (mode === "compact") {
    return (
      <CompactSidebar
        activeItem={activeItem}
        activeChild={activeChild}
        initials={initials}
        avatarUrl={avatarUrl}
        navOverrides={navOverrides}
        navLinks={navLinks}
        navItemIds={navItemIds}
        onNavItemClick={onNavItemClick}
      />
    );
  }
  if (mode === "quiet") {
    return (
      <QuietSidebar
        activeItem={activeItem}
        activeChild={activeChild}
        initials={initials}
        avatarUrl={avatarUrl}
        navOverrides={navOverrides}
        navLinks={navLinks}
        navItemIds={navItemIds}
        onNavItemClick={onNavItemClick}
      />
    );
  }

  // Apply overrides to produce the final nav items list
  const resolvedNavItems = resolveNavItems(NAV_ITEMS, navOverrides);

  // Auto-expand sections that contain the active child
  const defaultExpanded = resolvedNavItems
    .filter(
      (item) =>
        item.children &&
        (item.label === activeItem ||
          item.children.some((c) => c === activeChild))
    )
    .map((item) => item.label);

  const [expandedSections, setExpandedSections] =
    useState<string[]>(defaultExpanded);

  const toggleSection = (label: string) => {
    setExpandedSections((prev) =>
      prev.includes(label) ? prev.filter((s) => s !== label) : [...prev, label]
    );
  };

  return (
    <div
      className="bg-fs-surface-primary sticky top-0 flex h-screen w-[256px] shrink-0 flex-col border-r"
      style={{ borderColor: "var(--slate-color-border-muted, #DFE0E1)" }}
    >
      {/* Logo + actions */}
      <div className="flex items-center justify-between px-[var(--slate-dimensions-6,24px)] py-[var(--slate-dimensions-8,32px)]">
        <img src="/images/faire-logo.svg" alt="Faire" className="w-[88px]" />
        <div className="flex items-center gap-[var(--slate-dimensions-2,8px)]">
          <button
            className="relative flex h-10 w-10 items-center justify-center rounded-full transition-colors"
            style={{ background: "transparent" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background =
                "var(--slate-color-action-surface-inverse-hover, #f7f7f7)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            <BellIcon />
            <div
              className="absolute top-2 right-2 h-2 w-2 rounded-full"
              style={{
                backgroundColor:
                  "var(--slate-color-message-icon-critical, #E53935)",
              }}
            />
          </button>
          <button
            className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-full text-[13px] font-medium"
            style={{
              ...slateSans,
              backgroundColor: avatarUrl ? "transparent" : "#E8D5C4",
              color: "var(--slate-color-text-primary, #333)",
            }}
          >
            {avatarUrl ? (
              <>
                <img
                  src={avatarUrl}
                  alt={initials}
                  className="h-full w-full object-cover"
                />
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 rounded-full"
                  style={{
                    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
                  }}
                />
              </>
            ) : (
              initials
            )}
          </button>
        </div>
      </div>

      {/* Navigation — px-2 gives inset so hover backgrounds don't touch sidebar edges */}
      <div className="flex-1 overflow-y-auto px-2">
        <nav className="space-y-[2px]">
          {resolvedNavItems.map((item) => {
            const isExpanded = expandedSections.includes(item.label);
            const isActive = item.label === activeItem;
            const hasChildren = !!item.children;

            return (
              <div key={item.label}>
                <a
                  id={navItemIds[item.label]}
                  href={navLinks[item.label] ?? "#"}
                  className="flex items-center justify-between rounded-lg px-4 py-[6px] text-[14px] leading-[20px] transition-colors"
                  style={{
                    ...slateSans,
                    color: "var(--slate-color-text-primary, #333)",
                    fontWeight: isActive ? 500 : 400,
                    backgroundColor:
                      isActive && (!hasChildren || !activeChild)
                        ? "var(--slate-color-grey-300, #f5f5f5)"
                        : "transparent",
                  }}
                  onClick={(e) => {
                    if (onNavItemClick) {
                      e.preventDefault();
                      onNavItemClick(item.label);
                    } else if (!navLinks[item.label]) {
                      e.preventDefault();
                    }
                    if (hasChildren) {
                      toggleSection(item.label);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive || (hasChildren && activeChild)) {
                      e.currentTarget.style.backgroundColor =
                        "var(--slate-color-grey-300, #f5f5f5)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive || (hasChildren && activeChild)) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  <span>{item.label}</span>
                  <div className="flex items-center gap-[var(--slate-dimensions-2,8px)]">
                    {item.badge && (
                      <span
                        className="text-fs-text-primary-inverse flex h-[20px] min-w-[20px] items-center justify-center rounded-full px-[6px] text-[12px] font-medium"
                        style={{
                          ...slateSans,
                          backgroundColor:
                            "var(--slate-color-action-surface-default, #333)",
                        }}
                      >
                        {item.badge}
                      </span>
                    )}
                    {hasChildren && <ChevronIcon expanded={isExpanded} />}
                  </div>
                </a>

                {/* Children */}
                {hasChildren && isExpanded && (
                  <div className="mt-[2px] space-y-[2px]">
                    {item.children!.map((child) => {
                      const isChildActive = child === activeChild;
                      return (
                        <a
                          key={child}
                          href={navLinks[child] ?? "#"}
                          className="block rounded-lg py-[6px] pr-4 pl-8 text-[14px] leading-[20px] transition-colors"
                          style={{
                            ...slateSans,
                            color: "var(--slate-color-text-primary, #333)",
                            fontWeight: isChildActive ? 500 : 400,
                            backgroundColor: isChildActive
                              ? "var(--slate-color-grey-300, #f5f5f5)"
                              : "transparent",
                          }}
                          onClick={(e) => {
                            if (!navLinks[child]) e.preventDefault();
                          }}
                          onMouseEnter={(e) => {
                            if (!isChildActive) {
                              e.currentTarget.style.backgroundColor =
                                "var(--slate-color-grey-300, #f5f5f5)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isChildActive) {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }
                          }}
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <span className="min-w-0 flex-1 truncate">
                              {child}
                            </span>
                            {childBadges?.[child] != null &&
                            childBadges[child] !== "" ? (
                              <span
                                className="shrink-0 rounded border border-[#e8e8e8] bg-[#f5f5f5] px-1.5 py-0.5 text-[11px] leading-none font-medium text-[#333]"
                                style={slateSans}
                              >
                                {childBadges[child]}
                              </span>
                            ) : null}
                          </span>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      {/* Footer */}
      <div
        className="space-y-[2px] border-t px-2 py-3"
        style={{ borderColor: "var(--slate-color-border-muted, #DFE0E1)" }}
      >
        {FOOTER_ITEMS.map((label) => (
          <a
            key={label}
            href="#"
            className="block rounded-lg px-4 py-[6px] text-[14px] leading-[20px] transition-colors"
            style={{
              ...slateSans,
              color: "var(--slate-color-text-primary, #333)",
            }}
            onClick={(e) => e.preventDefault()}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                "var(--slate-color-grey-300, #f5f5f5)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            {label}
          </a>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  BrandPortalLayout — convenience wrapper                            */
/* ------------------------------------------------------------------ */

export interface BrandPortalLayoutProps {
  children: React.ReactNode;
  /** Which top-level nav item is active */
  activeItem?: string;
  /** Which child nav item is active */
  activeChild?: string;
  /** Avatar initials */
  initials?: string;
  /** Optional avatar image URL — when provided, replaces the initials circle. */
  avatarUrl?: string;
  /** Prototype-specific nav overrides (see NavItemOverride) */
  navOverrides?: NavItemOverride[];
  navLinks?: Record<string, string>;
  navItemIds?: Record<string, string>;
  onNavItemClick?: (label: string) => void;
  /**
   * Optional child nav pills (e.g. `{ Promotions: "New" }` — see `BrandPortalSidebarProps.childBadges`).
   */
  childBadges?: Record<string, string>;
  /** Sidebar rendering mode (see BrandPortalSidebarMode). Defaults to "full". */
  mode?: BrandPortalSidebarMode;
}

/**
 * Full Brand Portal layout with sidebar + content area.
 * Wrap your prototype page content in this component.
 */
export function BrandPortalLayout({
  children,
  activeItem,
  activeChild,
  initials,
  avatarUrl,
  navOverrides,
  navLinks,
  navItemIds,
  onNavItemClick,
  childBadges,
  mode,
}: BrandPortalLayoutProps) {
  return (
    <div className="flex h-screen overflow-hidden">
      <BrandPortalSidebar
        activeItem={activeItem}
        activeChild={activeChild}
        initials={initials}
        avatarUrl={avatarUrl}
        navOverrides={navOverrides}
        navLinks={navLinks}
        navItemIds={navItemIds}
        onNavItemClick={onNavItemClick}
        childBadges={childBadges}
        mode={mode}
      />
      <main className="min-h-0 min-w-0 flex-1 overflow-y-auto">{children}</main>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared visual helpers used by the alternate sidebar modes         */
/* ------------------------------------------------------------------ */

const BORDER_VAR = "var(--slate-color-border-muted, #DFE0E1)";
const TEXT_PRIMARY_VAR = "var(--slate-color-text-primary, #333)";
const TEXT_SUBDUED_VAR = "var(--slate-color-text-subdued, #757575)";
const GREY_300_VAR = "var(--slate-color-grey-300, #f5f5f5)";
const NOTIF_RED_VAR = "var(--slate-color-message-icon-critical, #E53935)";

function FCircleLogo({ size = 28 }: { size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        backgroundColor: "#1a1a1a",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontSize: Math.round(size * 0.5),
        fontFamily: "'Times New Roman', 'Faire Serif', serif",
        fontWeight: 400,
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      F
    </div>
  );
}

function FSquareLogo({ size = 24 }: { size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 4,
        backgroundColor: "#1a1a1a",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontSize: Math.round(size * 0.55),
        fontFamily: "'Times New Roman', 'Faire Serif', serif",
        fontWeight: 400,
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      F
    </div>
  );
}

function ChevronToggle({
  direction = "right",
}: {
  direction?: "left" | "right";
}) {
  const path = direction === "right" ? "M6 4L10 8L6 12" : "M10 4L6 8L10 12";
  return (
    <button
      type="button"
      className="flex h-7 w-7 items-center justify-center rounded-full"
      style={{ background: "transparent", border: "none", cursor: "pointer" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = GREY_300_VAR)}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
        <path
          d={path}
          stroke={TEXT_PRIMARY_VAR}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function BellButton({ size = 40 }: { size?: number }) {
  return (
    <button
      type="button"
      className="relative flex items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        background: "transparent",
        border: "none",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = GREY_300_VAR)}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <BellIcon />
      <div
        className="absolute top-2 right-2 h-2 w-2 rounded-full"
        style={{ backgroundColor: NOTIF_RED_VAR }}
      />
    </button>
  );
}

function AvatarCircle({
  initials,
  avatarUrl,
  size = 36,
}: {
  initials: string;
  avatarUrl?: string;
  size?: number;
}) {
  return (
    <div
      className="relative flex items-center justify-center overflow-hidden rounded-full text-[13px] font-medium"
      style={{
        ...slateSans,
        width: size,
        height: size,
        backgroundColor: avatarUrl ? "transparent" : "#E8D5C4",
        color: TEXT_PRIMARY_VAR,
        flexShrink: 0,
      }}
    >
      {avatarUrl ? (
        <>
          <img
            src={avatarUrl}
            alt={initials}
            className="h-full w-full object-cover"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }}
          />
        </>
      ) : (
        initials
      )}
    </div>
  );
}

function HelpIconButton() {
  return (
    <button
      type="button"
      className="flex h-9 w-9 items-center justify-center rounded-full"
      style={{ background: "transparent", border: "none", cursor: "pointer" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = GREY_300_VAR)}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <svg width={20} height={20} viewBox="0 0 24 24" fill="none">
        <circle
          cx="12"
          cy="12"
          r="9"
          stroke={TEXT_PRIMARY_VAR}
          strokeWidth="1.5"
        />
        <path
          d="M9.5 9.5a2.5 2.5 0 1 1 4 2c-.8.5-1.5 1-1.5 2"
          stroke={TEXT_PRIMARY_VAR}
          strokeWidth="1.5"
          strokeLinecap="round"
          fill="none"
        />
        <circle cx="12" cy="17" r="0.75" fill={TEXT_PRIMARY_VAR} />
      </svg>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Sidebar mode A — Icons + hover-to-peek                             */
/*  64px column with icon-only nav. Hovering expands to 256px overlay  */
/*  so labels appear without pushing the main content sideways.        */
/* ------------------------------------------------------------------ */

const ICONS_NARROW = 64;
const ICONS_PEEK = 256;

function HiddenSidebar({
  activeItem,
  initials = "AD",
  avatarUrl,
  navOverrides = [],
  navLinks = {},
}: BrandPortalSidebarProps) {
  const [isPeeking, setIsPeeking] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const resolvedNavItems = resolveNavItems(NAV_ITEMS, navOverrides);
  const isOpen = isPinned || isPeeking;

  const renderRow = (
    label: string,
    Icon: IconComponent,
    href: string,
    isActive: boolean
  ) => (
    <a
      key={label}
      href={href}
      className="flex items-center gap-3 rounded-lg transition-colors"
      style={{
        ...slateSans,
        // Content is always laid out at PEEK width; the container's
        // overflow:hidden masks the labels when narrow.
        width: ICONS_PEEK - 16,
        padding: "8px 12px",
        color: TEXT_PRIMARY_VAR,
        fontSize: 14,
        fontWeight: isActive ? 500 : 400,
        backgroundColor: isActive ? GREY_300_VAR : "transparent",
        textDecoration: "none",
      }}
      onClick={(e) => {
        if (!navLinks[label]) e.preventDefault();
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = GREY_300_VAR;
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = "transparent";
        }
      }}
    >
      <span
        style={{
          width: 24,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={20} weight="regular" />
      </span>
      <span
        style={{
          whiteSpace: "nowrap",
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? "translateX(0)" : "translateX(-4px)",
          transition:
            "opacity 220ms cubic-bezier(0.16, 1, 0.3, 1) 60ms, transform 220ms cubic-bezier(0.16, 1, 0.3, 1) 60ms",
          pointerEvents: isOpen ? "auto" : "none",
        }}
      >
        {label}
      </span>
    </a>
  );

  return (
    <>
      {/* Layout reserve — narrow when unpinned (sidebar overlays), peek width when pinned (sidebar joins layout). */}
      <div
        style={{
          width: isPinned ? ICONS_PEEK : ICONS_NARROW,
          flexShrink: 0,
          transition: "width 280ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        aria-hidden
      />

      {/* Actual sidebar — fixed-positioned. Open width when peeking OR pinned; shadow only on overlay (peek). */}
      <div
        className="bg-fs-surface-primary fixed top-0 left-0 flex h-screen flex-col overflow-hidden border-r"
        style={{
          width: isOpen ? ICONS_PEEK : ICONS_NARROW,
          transition:
            "width 280ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 320ms cubic-bezier(0.16, 1, 0.3, 1)",
          borderColor: BORDER_VAR,
          boxShadow:
            isPeeking && !isPinned
              ? "4px 0 24px rgba(0,0,0,0.05), 1px 0 0 rgba(0,0,0,0.02)"
              : "0 0 0 rgba(0,0,0,0)",
          zIndex: 50,
        }}
        onMouseEnter={() => setIsPeeking(true)}
        onMouseLeave={() => setIsPeeking(false)}
      >
        {/* Header: F-square + chevron at top, bell below */}
        <div
          className="flex flex-col items-start gap-3 py-4"
          style={{
            paddingLeft: 16,
            paddingRight: 16,
            width: ICONS_PEEK,
            flexShrink: 0,
          }}
        >
          <div className="flex items-center" style={{ gap: 6 }}>
            <FSquareLogo />
            <button
              type="button"
              onClick={() => setIsPinned((p) => !p)}
              aria-label={isPinned ? "Unpin menu" : "Pin menu open"}
              style={{
                background: "transparent",
                border: "none",
                padding: 4,
                margin: -4,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--slate-color-text-subdued, #757575)",
              }}
            >
              <CaretDoubleRight
                size={12}
                style={{
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 280ms cubic-bezier(0.16, 1, 0.3, 1)",
                  flexShrink: 0,
                }}
              />
            </button>
          </div>
          <div style={{ marginLeft: -8 }}>
            <BellButton size={40} />
          </div>
        </div>

        {/* Nav items */}
        <div
          className="flex-1 overflow-y-auto"
          style={{ paddingLeft: 8, paddingRight: 8 }}
        >
          <nav className="flex flex-col gap-[2px]">
            {resolvedNavItems.map((item) => {
              const Icon = NAV_ICONS[item.label] ?? Circle;
              const isActive = item.label === activeItem;
              return renderRow(
                item.label,
                Icon,
                navLinks[item.label] ?? "#",
                isActive
              );
            })}
          </nav>
        </div>

        {/* Footer: Help Center + What's new */}
        <div
          className="border-t"
          style={{
            borderColor: BORDER_VAR,
            paddingLeft: 8,
            paddingRight: 8,
            paddingTop: 8,
            paddingBottom: 8,
          }}
        >
          {FOOTER_ITEMS.map((label) => {
            const Icon = NAV_ICONS[label] ?? Circle;
            return renderRow(label, Icon, "#", false);
          })}
        </div>

        {/* Avatar at very bottom */}
        <div
          className="flex items-center"
          style={{
            paddingLeft: 20,
            paddingRight: 16,
            paddingTop: 12,
            paddingBottom: 16,
            gap: 12,
          }}
        >
          <AvatarCircle initials={initials} avatarUrl={avatarUrl} size={32} />
          <span
            style={{
              ...slateSans,
              fontSize: 14,
              color: TEXT_PRIMARY_VAR,
              whiteSpace: "nowrap",
              opacity: isPeeking ? 1 : 0,
              transform: isPeeking ? "translateX(0)" : "translateX(-4px)",
              transition:
                "opacity 220ms cubic-bezier(0.16, 1, 0.3, 1) 60ms, transform 220ms cubic-bezier(0.16, 1, 0.3, 1) 60ms",
              pointerEvents: isPeeking ? "auto" : "none",
            }}
          >
            {initials}
          </span>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Sidebar mode B — Compact text                                      */
/*  ~140px column. Text-only nav, no badges, no expand chevrons.       */
/* ------------------------------------------------------------------ */

function CompactSidebar({
  activeItem,
  initials = "AD",
  avatarUrl,
  navOverrides = [],
  navLinks = {},
  navItemIds = {},
  onNavItemClick,
}: BrandPortalSidebarProps) {
  const resolvedNavItems = resolveNavItems(NAV_ITEMS, navOverrides);

  return (
    <div
      className="bg-fs-surface-primary sticky top-0 flex h-screen w-[140px] shrink-0 flex-col border-r"
      style={{ borderColor: BORDER_VAR }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-5">
        <img src="/images/faire-logo.svg" alt="Faire" className="w-[60px]" />
        <BellButton size={28} />
      </div>

      {/* Nav — text only */}
      <div className="flex-1 overflow-y-auto px-2">
        <nav className="space-y-[2px]">
          {resolvedNavItems.map((item) => {
            const isActive = item.label === activeItem;
            return (
              <a
                id={navItemIds[item.label]}
                key={item.label}
                href={navLinks[item.label] ?? "#"}
                className="block rounded-lg px-3 py-[6px] text-[13px] leading-[18px] transition-colors"
                style={{
                  ...slateSans,
                  color: TEXT_PRIMARY_VAR,
                  fontWeight: isActive ? 500 : 400,
                  backgroundColor: isActive ? GREY_300_VAR : "transparent",
                }}
                onClick={(e) => {
                  if (onNavItemClick) {
                    e.preventDefault();
                    onNavItemClick(item.label);
                  } else if (!navLinks[item.label]) {
                    e.preventDefault();
                  }
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = GREY_300_VAR;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                {item.label}
              </a>
            );
          })}
        </nav>
      </div>

      {/* Avatar */}
      <div className="flex items-center px-3 py-3">
        <AvatarCircle initials={initials} avatarUrl={avatarUrl} size={30} />
      </div>

      {/* Footer with divider */}
      <div
        className="space-y-[2px] border-t px-2 py-3"
        style={{ borderColor: BORDER_VAR }}
      >
        {FOOTER_ITEMS.map((label) => (
          <a
            key={label}
            href="#"
            className="block rounded-lg px-3 py-[4px] text-[12px] leading-[16px] transition-colors"
            style={{ ...slateSans, color: TEXT_PRIMARY_VAR }}
            onClick={(e) => e.preventDefault()}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = GREY_300_VAR)
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            {label}
          </a>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sidebar mode C — Quiet rail                                        */
/*  256px column (unchanged width). Lighter type, no badges/chevrons.  */
/* ------------------------------------------------------------------ */

function QuietSidebar({
  activeItem,
  initials = "AD",
  avatarUrl,
  navOverrides = [],
  navLinks = {},
  navItemIds = {},
  onNavItemClick,
}: BrandPortalSidebarProps) {
  const resolvedNavItems = resolveNavItems(NAV_ITEMS, navOverrides);

  return (
    <div
      className="bg-fs-surface-primary sticky top-0 flex h-screen w-[256px] shrink-0 flex-col border-r"
      style={{ borderColor: BORDER_VAR }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-[var(--slate-dimensions-6,24px)] py-[var(--slate-dimensions-8,32px)]">
        <img src="/images/faire-logo.svg" alt="Faire" className="w-[88px]" />
        <BellButton />
      </div>

      {/* Nav — quieter styling */}
      <div className="flex-1 overflow-y-auto px-2">
        <nav className="space-y-[4px]">
          {resolvedNavItems.map((item) => {
            const isActive = item.label === activeItem;
            return (
              <a
                id={navItemIds[item.label]}
                key={item.label}
                href={navLinks[item.label] ?? "#"}
                className="block rounded-lg px-4 py-[8px] text-[14px] leading-[20px]"
                style={{
                  ...slateSans,
                  color: isActive ? TEXT_PRIMARY_VAR : TEXT_SUBDUED_VAR,
                  fontWeight: 400,
                  backgroundColor: "transparent",
                }}
                onClick={(e) => {
                  if (onNavItemClick) {
                    e.preventDefault();
                    onNavItemClick(item.label);
                  } else if (!navLinks[item.label]) {
                    e.preventDefault();
                  }
                }}
              >
                {item.label}
              </a>
            );
          })}
        </nav>
      </div>

      {/* Avatar */}
      <div className="flex items-center px-[var(--slate-dimensions-6,24px)] py-3">
        <AvatarCircle initials={initials} avatarUrl={avatarUrl} />
      </div>

      {/* Footer with divider */}
      <div
        className="space-y-[2px] border-t px-2 py-3"
        style={{ borderColor: BORDER_VAR }}
      >
        {FOOTER_ITEMS.map((label) => (
          <a
            key={label}
            href="#"
            className="block rounded-lg px-4 py-[6px] text-[14px] leading-[20px]"
            style={{ ...slateSans, color: TEXT_SUBDUED_VAR }}
            onClick={(e) => e.preventDefault()}
          >
            {label}
          </a>
        ))}
      </div>
    </div>
  );
}
